// Admin Portal — BuildIQ facility configuration panel. Rendered from
// FacilityDetail when the facility has a construction service. Talks to the
// BuildIQ service's /api/settings endpoints (platform admin token honored).
// Secrets are write-only: blank keeps the stored value; keys are rotated
// server-side, never typed.
import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { BUILDIQ_API_URL } from "../../constants";

const inputCls =
  "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

// Canonical /api/fn/* (hyveai-conventions SKILL.md). These showed
// /.netlify/functions/* until 2026-09-10 and worked only through the
// deprecated alias in app/main.py. That alias STAYS - existing webhooks and
// calendar subscriptions in the wild still point at the old path.
const KEY_META = [
  {
    id: "lead_key",
    field: "lead_key",
    label: "Lead intake key",
    url: (base, v) => `${base}/api/fn/lead?k=${v}`,
    hint: "Give this URL (or the key as an x-buildiq-lead-key header) to the marketing site's form.",
  },
  {
    id: "ics_key",
    field: "ics_key",
    label: "Calendar feed key",
    url: (base, v) => `${base}/api/fn/ics?k=${v}`,
    hint: "Rotating it breaks existing phone-calendar subscriptions.",
  },
  {
    id: "twilio_webhook",
    field: ["twilio", "webhook_key"],
    label: "Twilio inbound key",
    url: (base, v) => `${base}/api/fn/twilio?k=${v}`,
    hint: "Set as the Twilio number's “A message comes in” webhook URL.",
  },
  {
    id: "cc_webhook",
    field: ["companycam", "webhook_key"],
    label: "CompanyCam webhook key",
    url: (base, v) => `${base}/api/fn/cc-webhook?k=${v}`,
    hint: "Registered automatically by the app's “Connect photo webhook” button.",
  },
];

const getPath = (obj, field) =>
  Array.isArray(field) ? (obj[field[0]] || {})[field[1]] : obj[field];

export const BuildIQConfig = ({ facilityId }) => {
  const { token } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({});
  const [twilioToken, setTwilioToken] = useState("");
  const [ccToken, setCcToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(null);
  const [msg, setMsg] = useState(null);

  const api = async (path, options = {}) => {
    const res = await fetch(`${BUILDIQ_API_URL}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || data.error || `HTTP ${res.status}`);
    return data;
  };

  const load = () =>
    api(`/api/settings?facility_id=${facilityId}`)
      .then((d) => {
        setCfg(d);
        setForm({
          company_name: d.company_name || "",
          license_line: d.license_line || "",
          footer_line: d.footer_line || "",
          default_salesman: d.default_salesman || "",
          logo_url: d.logo_url || "",
          autosend: d.autosend === true,
          twilio_sid: d.twilio?.sid || "",
          twilio_from: d.twilio?.from || "",
        });
      })
      .catch((e) => setMsg({ type: "err", text: e.message }));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const body = {
        facility_id: facilityId,
        company_name: form.company_name,
        license_line: form.license_line,
        footer_line: form.footer_line,
        default_salesman: form.default_salesman,
        logo_url: form.logo_url,
        autosend: form.autosend,
        twilio: { sid: form.twilio_sid, from: form.twilio_from },
      };
      if (twilioToken.trim()) body.twilio.token = twilioToken.trim();
      if (ccToken.trim()) body.companycam = { token: ccToken.trim() };
      const d = await api("/api/settings", {
        method: "PUT",
        body: JSON.stringify(body),
      });
      setCfg(d);
      setTwilioToken("");
      setCcToken("");
      setMsg({ type: "ok", text: "Saved." });
    } catch (e) {
      setMsg({ type: "err", text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const rotate = async (id) => {
    if (
      !window.confirm(
        "Rotate this key? Anything using the old key (webhooks, calendar subscriptions, form integrations) stops working until updated.",
      )
    )
      return;
    setRotating(id);
    try {
      await api("/api/settings/rotate", {
        method: "POST",
        body: JSON.stringify({ facility_id: facilityId, key: id }),
      });
      await load();
    } catch (e) {
      setMsg({ type: "err", text: e.message });
    } finally {
      setRotating(null);
    }
  };

  if (!cfg)
    return (
      <div className="p-4 text-sm text-slate-400">Loading BuildIQ config…</div>
    );

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5">
      <div>
        <h3 className="font-semibold text-slate-800">BuildIQ Configuration</h3>
        <p className="text-xs text-slate-400">
          Branding, integrations and automation settings for this facility's
          construction suite.
        </p>
      </div>
      {msg && (
        <p
          className={`text-sm ${msg.type === "ok" ? "text-green-600" : "text-red-600"}`}
        >
          {msg.text}
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Company name</label>
          <input
            className={inputCls}
            value={form.company_name}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
            placeholder="Shown in SMS copy, calendar feed, portal"
          />
        </div>
        <div>
          <label className={labelCls}>Default salesman</label>
          <input
            className={inputCls}
            value={form.default_salesman}
            onChange={(e) =>
              setForm({ ...form, default_salesman: e.target.value })
            }
            placeholder="Pre-filled on website leads"
          />
        </div>
        <div>
          <label className={labelCls}>License line</label>
          <input
            className={inputCls}
            value={form.license_line}
            onChange={(e) => setForm({ ...form, license_line: e.target.value })}
            placeholder="e.g. Licensed, Bonded & Insured — ROC #123456"
          />
        </div>
        <div>
          <label className={labelCls}>Footer line</label>
          <input
            className={inputCls}
            value={form.footer_line}
            onChange={(e) => setForm({ ...form, footer_line: e.target.value })}
          />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Logo URL</label>
          <input
            className={inputCls}
            value={form.logo_url}
            onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
            placeholder="https://… — shown in the app header and on generated documents"
          />
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg p-4 space-y-3">
        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
          Twilio (sub confirmations + client reminders)
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Account SID</label>
            <input
              className={inputCls}
              value={form.twilio_sid}
              onChange={(e) => setForm({ ...form, twilio_sid: e.target.value })}
            />
          </div>
          <div>
            <label className={labelCls}>From number</label>
            <input
              className={inputCls}
              value={form.twilio_from}
              onChange={(e) =>
                setForm({ ...form, twilio_from: e.target.value })
              }
              placeholder="+14805551212"
            />
          </div>
          <div>
            <label className={labelCls}>Auth token</label>
            <input
              type="password"
              autoComplete="new-password"
              className={inputCls}
              value={twilioToken}
              onChange={(e) => setTwilioToken(e.target.value)}
              placeholder={
                cfg.twilio?.has_token ? "Configured — blank keeps it" : "Not set"
              }
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={form.autosend}
            onChange={(e) => setForm({ ...form, autosend: e.target.checked })}
            className="rounded"
          />
          Enable automated daily client reminders (requires Twilio credentials)
        </label>
      </div>

      <div className="border border-slate-200 rounded-lg p-4 space-y-3">
        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
          CompanyCam
        </p>
        <div>
          <label className={labelCls}>Access token</label>
          <input
            type="password"
            autoComplete="new-password"
            className={inputCls}
            value={ccToken}
            onChange={(e) => setCcToken(e.target.value)}
            placeholder={
              cfg.companycam?.has_token ? "Configured — blank keeps it" : "Not set"
            }
          />
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg p-4 space-y-3">
        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
          Integration keys & webhook URLs
        </p>
        {KEY_META.map((k) => {
          const value = getPath(cfg, k.field) || "";
          return (
            <div key={k.id} className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-600 w-44 shrink-0">
                  {k.label}
                </span>
                <input
                  readOnly
                  value={value ? k.url(BUILDIQ_API_URL, value) : "— not generated —"}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 px-2 py-1.5 border border-slate-200 rounded-lg text-xs font-mono text-slate-600 bg-slate-50"
                />
                <button
                  type="button"
                  onClick={() => rotate(k.id)}
                  disabled={rotating === k.id}
                  className="px-2.5 py-1.5 text-xs font-medium border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  {rotating === k.id ? "…" : value ? "Rotate" : "Generate"}
                </button>
              </div>
              <p className="text-[11px] text-slate-400 ml-44 pl-2">{k.hint}</p>
            </div>
          );
        })}
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save BuildIQ Config"}
      </button>
    </div>
  );
};
